import _ from "lodash";
import i18n from "../i18n";

import {
  getFailed,
  getStarted,
  getSucceeded,
  getEnded,
  MESSAGING_INITIALIZATION,
  ON_CHANGE_MESSAGE_FORM,
  ON_SUBMIT_MESSAGE,
  MESSAGE_TOGGLE,
  MESSAGE_DELETE,
  CLEAR_MESSAGE,
  MESSAGE_SAVE_DRAFT,
  SELECTED_FOR_EDIT_MESSAGE,
  MESSAGE_FILTER,
  MESSAGE_PUBLISH_DRAFT,
  CLEAR_DIRTY_DRAFT,
  IS_UNSAVED,
  RECALC_REQUEST,
  RECALC_SUCCESS,
  CLEAR_RECALC_SUCCESS,
  RECALC_FAILURE,
} from "../actions/constant";
import moment from "moment";

const initialState = {
  messageForm: {},
  isDraft: false,
  isLoading: false,
  reply: false,
  forumPost: {},
  isSelected: false,
  selectedConversation: "",
  selectedPost: "",
  counterState: 0,
  draftId: "",
  published: false,
  newDraft: false,
  selectedDraft: undefined,
  draftStatus: false,
  isDirty: false,
  searchCount: 0,
  recalcError: null,
};
function orderedListMaker(messagesFilter, sortBy) {
  const findSender = (item) => {
    const loggedUser = JSON.parse(localStorage.getItem("user"));

    const userID = loggedUser._id;
    if (item.messages) {
      const senderFind = item.messages.find((m) => m.sender._id !== userID);
      if (senderFind) {
        return senderFind.sender._id;
      } else if (item.post && item.post.user && item.post.user !== userID) {
        return item.post.user;
      }
    }
  };
  if (sortBy == "null" || sortBy == "new") {
    return _.orderBy(messagesFilter, ["date"], ["desc"]);
  } else if (sortBy == "old") {
    return _.orderBy(messagesFilter, ["date"], ["asc"]);
  } else if (sortBy == "ascending") {
    return _.orderBy(
      messagesFilter,
      [(item) => item.type !== "company" && findSender(item)],
      ["asc", "asc"]
    );
  } else if (sortBy == "descending") {
    return _.orderBy(
      messagesFilter,
      [(item) => item.type !== "company" && findSender(item)],
      ["desc", "asc"]
    );
  } else {
    return _.orderBy(messagesFilter, [sortBy, "new"], ["desc"]);
  }
}

const asNumber = (id) => {
  if (id == null) return null;
  const s = String(id).replace(/^0+/, ""); // keep numeric meaning
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const latestTimestamp = (item) => {
  // 1) Prefer latest message in the container
  const msgTimes = (item && item.messages || [])
    .map((m) => (m && (m.date || m.createdAt)))
    .map((d) => (d == null ? NaN : Number(d)))
    .filter((n) => Number.isFinite(n));
  if (msgTimes.length) return Math.max(...msgTimes);

  // 2) No messages → fall back to container (title) date
  if (item && item.date != null) return Number(item.date);

  // 3) Finally, fall back to the post’s date
  if (item && item.post && item.post.date != null) return Number(item.post.date);

  return 0;
};

const senderBucketAndId = (item, myId) => {
  // Bucket + "id" we sort by when bucket === 'user'
  // company/TCP are always 12u12; feedback only 12u12 if there's at least one admin reply
  if (item.type === "company" || item.type === "TCP" || item.type === "update_nutrient" || item.type === "update_name") {
    return { bucket: "twelve", id: null };
  }
  if (item.type === "feedback") {
    const hasAdmin =
      (item.messages || []).some(
        (m) => String(m && m.sender).toLowerCase() === "12u12"
      );
    return { bucket: hasAdmin ? "twelve" : "none", id: null };
  }
  // personal: other participant id (not me)
  let other = null;
  for (const m of item.messages || []) {
    const sid = m && m.sender && m.sender._id;
    const rid = m && m.receiver && m.receiver._id;
    if (sid && sid !== myId) { other = sid; break; }
    if (rid && rid !== myId) { other = rid; break; }
  }
  if (!other && item && item.post && item.post.user && item.post.user !== myId) other = item.post.user;

  return other ? { bucket: "user", id: other } : { bucket: "none", id: null };
};

// --- main: unified sorting per spec ---
function messagesOrder(messages, sort) {
  const sortBy = !sort || sort === "null" ? "new" : sort;
  const logged = JSON.parse(localStorage.getItem("user")) || {};
  const myId = logged._id;

  const arr = [...messages];

  if (sortBy === "new" || sortBy === "old") {
    arr.sort((a, b) => {
      const da = latestTimestamp(a);
      const db = latestTimestamp(b);
      return sortBy === "new" ? db - da : da - db;
    });
    return arr;
  }

  // Sender-ID sorts:
  // ascending: user -> twelve -> none
  // descending: twelve -> user -> none
  const forAscending = sortBy === "ascending";
  const groupRank = (bucket) => {
    if (forAscending) {
      return bucket === "user" ? 1 : bucket === "twelve" ? 2 : 3;
    } else {
      return bucket === "twelve" ? 1 : bucket === "user" ? 2 : 3;
    }
  };

  arr.sort((a, b) => {
    const A = senderBucketAndId(a, myId);
    const B = senderBucketAndId(b, myId);

    const rA = groupRank(A.bucket);
    const rB = groupRank(B.bucket);
    if (rA !== rB) return rA - rB;

    if (A.bucket === "user" && B.bucket === "user") {
      const aNum = asNumber(A.id);
      const bNum = asNumber(B.id);
      if (aNum != null && bNum != null && aNum !== bNum) {
        // ascending  -> user IDs ascending
        // descending  -> user IDs descending
        return forAscending ? (aNum - bNum) : (bNum - aNum);
      }
      // same id (or non-numeric) -> newest first
      return latestTimestamp(b) - latestTimestamp(a);
    }

    // 'twelve' and 'none' groups: newest first
    return latestTimestamp(b) - latestTimestamp(a);
  });

  return arr;
}

// normalize to lower-case text (leave punctuation alone; we want substring)
const norm = (s) => String(s || "").toLowerCase();

// Titles that need the 'link' carve-out for company posts
const COMPANY_LINK_EXCEPTION_TITLES = new Set([
  // English
  'archived post republished',
  'irrelevant post deleted',
  // Chinese (Simplified)
  '存档帖子已重新发布',
  '不相关帖子删除',
  '链接帖子已重新发布',  // Linked post republished, only exclude the chinese version
  // Chinese (Traditional)
  '存檔帖子已重新發布',
  '不相關帖子刪除',
  '鏈接帖子已重新發布',  // Linked post republished, only exclude the chinese version
]);

/**
 * Build an array of individually searchable fields (lowercased).
 * - Adds "you" and "你" tokens if this container involves the current user (myId)
 * - Includes sender/receiver/post user IDs, EXCEPT your own id
 * - Includes "12u12" for company/feedback containers
 * - Applies the 'link' carve-out on company posts with the two specific titles
 */
function collectVisibleFields(item, myId, qLower) {
  const fields = [];
  const type = item && item.type;
  const titleLower = norm(item && item.title);
  const qHasLink = qLower.includes("link");

  // Common: title/content/messages text
  if (type === "company" || type === "TCP" || type === "update_nutrient" || item.type === "update_name") {
    // Always let "12u12" be searchable for company/TCP surface
    fields.push("12u12");
    if (item && item.title) fields.push(item.title);

    if (item && item.post_id) {
      // 'link' carve-out for two special titles
      const isExceptionTitle = COMPANY_LINK_EXCEPTION_TITLES.has(titleLower);
      const allowContent = !(isExceptionTitle && qHasLink);
      if (allowContent && item && item.content) fields.push(item.content);
    } else {
      // Non-post company messages
      if (item && item.content) fields.push(item.content);

      (item && item.messages || []).forEach((m) => {
        if (m && m.content) fields.push(m.content);
        if (m && m.title) fields.push(m.title);
      });
    }

    // Make the "click here" label searchable for contribute_category_mismatch
    if (item.for === "contribute_category_mismatch") {
      const clickHere = i18n.t("support.CLICK_HERE_LINK_TEXT");
      fields.push(clickHere);
    }

    // Index TCP Trend Alert structured fields
    if (type === "TCP") {
      if (item.prefix)  { fields.push(item.prefix); }
      if (item.suffix)  { fields.push(item.suffix); }

      // Per-symptom blocks live under item.messages[]
      const blocks = Array.isArray(item.messages) && item.messages.length
        ? item.messages
        : [{
            symptom_label: item.symptom_label,
            cross_label:   item.cross_label,
            tik_label:     item.tik_label,
            symptom_title: item.symptom_title,
            cross_items:   item.cross_items,
            tik_items:     item.tik_items,
          }];


      blocks.forEach((b) => {
        if (b.symptom_label) fields.push(b.symptom_label);
        if (b.cross_label)   fields.push(b.cross_label);
        if (b.tik_label)     fields.push(b.tik_label);
        if (b.symptom_title) fields.push(b.symptom_title);
        (b.cross_items || []).forEach((x) => fields.push(x));
        (b.tik_items   || []).forEach((x) => fields.push(x));
      });
    }

  } else {
    // feedback / personal
    if (item && item.title) fields.push(item.title);
    if (item && item.post && item.post.body) fields.push(item.post.body);
    (item && item.messages || []).forEach((m) => {
      if (m && m.message) fields.push(m.message);
    });

    if (type === "feedback") {
      // Always index "you" for feedback (title submitted by the user)
      const youText = i18n.t("message.YOU_TEXT");
      fields.push(youText);

      // Index "12u12" ONLY if there is an admin reply
      const hasAdmin = (item && item.messages || []).some(
        (m) => String(m && m.sender).toLowerCase() === "12u12"
      );
      if (hasAdmin) fields.push("12u12");
    }
  }

  // Include user IDs (sender/receiver/post user), excluding myId
  (item && item.messages || []).forEach((m) => {
    const sId = m && m.sender && m.sender._id;
    const rId = m && m.receiver && m.receiver._id;
    if (sId && sId !== myId) fields.push(sId);
    if (rId && rId !== myId) fields.push(rId);
  });
  const postUser = item && item.post && item.post.user;
  if (postUser && postUser !== myId) fields.push(postUser);

  // If this container involves me, include "you" token
  const involvesMe =
    (item && item.messages || []).some(
      (m) =>
        (m && m.sender && m.sender._id === myId) ||
        (m && m.receiver && m.receiver._id === myId)
    ) || postUser === myId;

  if (involvesMe) {
    const youText = i18n.t("message.YOU_TEXT");
    fields.push(youText);
  }

  return fields.map(norm).filter(Boolean);
}

/**
 * Does a container match the search query?
 * Simple substring against the computed visible text (with special handling baked in).
 */
function containerMatchesVisible(item, qLower, myId) {
  if (!qLower) return true;
  // New behavior: phrase must appear in at least one *single* field.
  const fields = collectVisibleFields(item, myId, qLower);
  return fields.some((f) => f.includes(qLower));
}

// Reuse your existing sorting rules (must be non-mutating)
function sortContainers(items, sortBy) {
  const key = sortBy || "new";
  return messagesOrder([...items], key);
}

function reducer(state = initialState, action) {
  function unsavedDataChecker(type, newDraft) {
    let vAlertMessages = {
      title: i18n.t(
        newDraft
          ? "forum.DRAFT_UNSAVED_REPLY_TITLE"
          : "forum.DRAFT_UNSAVED_TITLE"
      ),
      text: i18n.t("message.DRAFT_NOT_SAVED_TEXT_3"),
      page: "inbox",
      newDraft,
    };
    if (type === "yes") {
      IS_UNSAVED("yes");
      localStorage.setItem("unsavedAlertData", JSON.stringify(vAlertMessages));
    } else {
      IS_UNSAVED("no");
      localStorage.removeItem("unsavedAlertData");
    }
  }
  switch (action.type) {
    case getStarted(MESSAGING_INITIALIZATION):
      return { ...state, isLoading: true };
    case getSucceeded(MESSAGING_INITIALIZATION):
      state.messages = action.payload.messages.data;

      state.forumPost = JSON.parse(localStorage.getItem("post"));
      localStorage.removeItem("post");
      let messageInitial = action.payload.messages.data.map((item) => {
        item.isExpand = false;
        item.isSelected = false;
        item.reply = item.messages.length > 0;
        item.messages.reverse().sort(function (a, b) {
          return b.date - a.date;
        });

        return item;
      });

      if (state.forumPost && state.forumPost != "null") {
        messageInitial.unshift({
          _id: "",
          post: {
            _id: state.forumPost._id,
            body: state.forumPost.body,
            date: state.forumPost.date * 1000,
            user: state.forumPost.user_id,
          },
          messages: [],
          date: moment(),
          isExpand: false,
          isSelected: true,
        });
      }

      return {
        ...state,
        messages: messageInitial,
        allMessages: messageInitial,
        filteredMessages: messageInitial,
        messageForm: { sortBy: "new" },
        isLoading: false,
        isSelected: false,
        counterState: state.counterState + 1,
      };
    case getEnded(MESSAGING_INITIALIZATION):
      return { ...state, isLoading: false };

    case ON_CHANGE_MESSAGE_FORM: {
      const myId = action.myId; // kept if you need it elsewhere
      const nextForm = { ...state.messageForm, [action.name]: action.value };

      // Compute newDraft now so prompts prior to save/update can use it
      const hasText = (nextForm.message || "").trim().length > 0;
      const isEditingDraft = !!state.draftId;
      const newDraftNext = !isEditingDraft && hasText;

      // === Freeze list while composing (no refilter/resort) ===
      if (action.name === "message") {
        const selectedDraft = state.selectedDraft;
        const currentMsg = (nextForm.message || "").trim();
        const dirty =
          selectedDraft !== undefined
            ? selectedDraft.trim() !== currentMsg
            : currentMsg.length > 0;

        // Use the *new* newDraft flag so prompts are correct
        if (dirty) {
          unsavedDataChecker("yes", newDraftNext);
        } else {
          unsavedDataChecker("no");
        }

        return {
          ...state,
          messageForm: nextForm,
          // DO NOT touch filteredMessages/messages while typing
          counterState: state.counterState + 1,
          isDirty: dirty,
          newDraft: newDraftNext,
          amountReseter: !state.amountReseter,
        };
      }

      // ===  Filter + sort when search/sort change  ===
      const qLower = norm(nextForm.search);
      const sortBy = nextForm.sortBy || "new";

      // Base is ALWAYS the full inbox now
      const base = state.allMessages || [];

      // Filter by visible text (includes id-search, you/你, 12u12, and company 'link' carve-out)
      const filtered = qLower
        ? base.filter((it) => containerMatchesVisible(it, qLower, myId))
        : base.slice();

      // Sort containers AFTER filtering (uses your existing sorters)
      const sorted = sortContainers(filtered, sortBy);

      // Dirty state is unchanged by search/sort; keep composer flags in sync
      const selectedDraft = state.selectedDraft;
      const currentMsg = (nextForm.message || "").trim();
      const dirty =
        selectedDraft !== undefined
          ? selectedDraft.trim() !== currentMsg
          : currentMsg.length > 0;

      if (dirty) {
        unsavedDataChecker("yes", newDraftNext);
      } else {
        unsavedDataChecker("no");
      }

      return {
        ...state,
        messageForm: nextForm,
        filteredMessages: sorted,
        messages: sorted,
        counterState: state.counterState + 1,
        isDirty: dirty,
        newDraft: newDraftNext,
        amountReseter: !state.amountReseter,
      };
    }


    case getStarted(ON_SUBMIT_MESSAGE):
      localStorage.removeItem("post");

      const draftStatus = !!(action.payload && action.payload[0] && action.payload[0].is_draft);

      // New vs Update:
      // If submitting a draft and we DON'T have a draftId yet -> it's a brand new draft.
      // If draftId exists -> we're updating an existing draft.
      const isNewDraft = draftStatus ? !state.draftId : false;

      return {
        ...state,
        reply: true,
        isLoading: true,
        draftStatus: draftStatus,    // drives showing the draft success modal in Message.js
        published: !draftStatus,     // publish=false for drafts, true for non-drafts
        draftSaved: draftStatus,     // legacy flag you already use
        newDraft: isNewDraft,        // <-- this drives "Saved" vs "Updated" text
        isDirty: false,
      };
    case getSucceeded(ON_SUBMIT_MESSAGE):
      unsavedDataChecker("no");
      return {
        ...state,
        reply: true,
        isLoading: false,
      };
    case getStarted(ON_SUBMIT_MESSAGE):
      return { ...state, isLoading: false };
    case MESSAGE_TOGGLE:
      const messageToggles = state.messages.map((message, index) => {
        if (message._id ? message._id === action.id : index === action.index) {
          message.isExpand = !message.isExpand;
        }
        return message;
      });
      return {
        ...state,
        messages: messageToggles,
      };

    case MESSAGE_DELETE:
      let selectedMessage = String(action.id || "");

      // Mark message to be deleted --> Return true only when there is an id and it equals the delete target
      function isMatch(id) {
        return id != null && String(id) === selectedMessage;
      }

      // Mark messages to be kept (not deleted) --> Return true when there's no ID or when ID doesn't match delete target
      function keepItem(item) {
        const conId = item && item._id;
        const postId = item && item.post && item.post._id;

        // Guard: if no ids exist, KEEP the item (do not delete)
        if (conId == null && postId == null) return true;

        return !(isMatch(conId) || isMatch(postId));
      }

      // Keeps every item for which keepItem(item) returns true
      let newMessageList = (state.filteredMessages || []).filter(keepItem);
      let newMessageList2 = (state.allMessages || []).filter(keepItem);

      // ✅ Clear selection flags in BOTH arrays (base + visible)
      const clearSel = (arr) => (arr || []).forEach((it) => { if (it) it.isSelected = false; });
      clearSel(newMessageList);
      clearSel(newMessageList2);

      newMessageList = newMessageList.map(it => ({ ...it, isSelected: false }));

      state.selectedIndex = -1;
      state.isSelected = false;
      state.isDraft = false;
      state.selectedConversation = "";
      state.selectedPost = "";
      state.reply = false;
      state.draftId = "";
      state.selectedDraft = undefined;
      state.messageForm.message = "";
      unsavedDataChecker("no");

      return {
        ...state,
        messages: newMessageList,
        filteredMessages: newMessageList,
        allMessages: newMessageList2,
        draftModal: false,
        saveSuccess: false,
        isDirty: false,
        newDraft: false,
      };
    case CLEAR_MESSAGE:
      state.messageForm.message = "";

      if (!state.newDraft) {
        unsavedDataChecker("yes");
      }
      return {
        ...state,
        counterState: state.counterState + 1,
        isDirty: state.newDraft ? false : true,
      };
    case MESSAGE_SAVE_DRAFT:
      state.isDraft = true;
      return {
        ...state,
        counterState: state.counterState + 1,
      };
    case CLEAR_DIRTY_DRAFT:
      unsavedDataChecker("no");
      return {
        ...state,
        isDirty: false,
        newDraft: false,
        published: false,
        draftStatus: false,
      };

    case SELECTED_FOR_EDIT_MESSAGE:
      let messageDetail = {};
      let selectedInd = action.index || 0;
      let allMessages = [...state.messages];

      // Reset draft state on any new selection attempt
      state.isDraft = false;
      state.draftId = "";            // ✅ clear stale draft id
      state.selectedDraft = undefined; // ✅ clear stale selectedDraft snapshot

      // Clear composer if something was previously selected
      if (state.selectedIndex !== -1) {
        state.messageForm.message = "";
      }

      // Clear all selection flags, then select the requested row
      for (const message of allMessages) {
        message.isSelected = false;
      }
      if (allMessages[selectedInd]) {
        allMessages[selectedInd].isSelected = true;
      }
      state.isSelected = true;

      // Toggle off if same row clicked again
      if (selectedInd === state.selectedIndex) {
        if (allMessages[selectedInd]) allMessages[selectedInd].isSelected = false;
        selectedInd = -1;
        state.selectedIndex = -1;
        state.isSelected = false;
        state.draftId = "";          // ✅ also clear on toggle-off
        state.selectedDraft = undefined;
      }

      // Capture the full container for downstream draft checks
      const messageEdit = allMessages.map((message) => {
        if (message._id === action.conId) {
          messageDetail = message;
        }
        return message;
      });

      // Only treat as "redirected forum post" when we're explicitly selecting
      // the synthetic, injected first row (no real ids provided).
      const target = allMessages[selectedInd];
      const selectingForumInjectedRow =
        state.forumPost &&
        state.forumPost != "null" &&
        target &&
        target._id === "" &&        // represents temporary container created by Frontend upon clicking envelope icon on Forum
        target.post &&              // represents the forum post
        String(target.post._id) === String(state.forumPost._id);
      
      if (selectingForumInjectedRow) {
        state.isSelected = true;
        state.isDraft = false;
        state.selectedConversation = "new";
        state.selectedPost = state.forumPost._id;
        state.reply = false;
        state.draftId = "";
        state.published = false;
        // note: we intentionally do not set messageForm.message here
      } else if (state.isSelected) {
        // Populate draft text if this container has a draft
        (messageDetail.messages || []).forEach((msg) => {
          if (msg && msg.is_draft) {
            state.isDraft = true;
            state.messageForm.message = msg.message;
            state.draftId = msg._id;
          }
        });
        state.selectedConversation = action.conId;
        state.selectedPost = action.postId;
      } else {
        // Fully cleared state when nothing is selected
        state.selectedConversation = "";
        state.messageForm.message = "";
        state.selectedPost = "";
        state.newDraft = false;
      }

      // Snapshot the currently loaded draft text (used for dirty checks)
      state.selectedDraft = state.isDraft ? state.messageForm.message : undefined;

      return {
        ...state,
        messages: messageEdit,
        selectedIndex: selectedInd,
        isDirty: false,
      };
      

    case MESSAGE_FILTER:
      let messagesFilter = state.filteredMessages;

      action.sortBy = action.sortBy == "null" ? "new" : action.sortBy;
      if (action.sortBy == "new") {
        messagesFilter = orderedListMaker(messagesFilter, action.sortBy);
        messagesFilter.map((item) => {
          item.messages.sort(function (a, b) {
            return b.date - a.date;
          });
        });
        messagesFilter.sort((a, b) => {
          let itemA = a.messages[0] ? a.messages[0].date : a.date;
          let itemB = b.messages[0] ? b.messages[0].date : b.date;
          return itemB - itemA;
        });
      } else if (action.sortBy == "old") {
        messagesFilter = orderedListMaker(messagesFilter, action.sortBy);

        messagesFilter.map((item) => {
          item.messages.sort(function (a, b) {
            return b.date - a.date;
          });
        });
        messagesFilter.sort((a, b) => {
          let itemA = a.messages[0] ? a.messages[0].date : a.date;
          let itemB = b.messages[0] ? b.messages[0].date : b.date;
          return itemA - itemB;
        });
      } else if (action.sortBy == "ascending") {
        messagesFilter = orderedListMaker(messagesFilter, "new");

        messagesFilter = messagesOrder(messagesFilter, action.sortBy);

        messagesFilter.map((item) => {
          item.messages.length > 0 &&
            item.messages.sort(function (a, b) {
              return (
                a.sender &&
                b.sender &&
                a.sender._id - b.sender._id &&
                b.date - a.date
              );
            });
        });
      } else if (action.sortBy == "descending") {
        messagesFilter = orderedListMaker(messagesFilter, "new");

        messagesFilter = messagesOrder(messagesFilter, action.sortBy);
        messagesFilter.map((item) => {
          item.messages.length > 0 &&
            item.messages.sort(function (a, b) {
              return (
                a.sender &&
                b.sender &&
                b.sender._id - a.sender._id &&
                b.date - a.date
              );
            });
        });
      }

      return {
        ...state,
        messages: messagesFilter,
        filteredMessages: messagesFilter,
        messageForm: {
          ...state.messageForm,      // keep current Text Area text & other fields
          sortBy: action.sortBy,    // update sort
          search: state.messageForm.search,
        },
      };
    case getStarted(MESSAGE_PUBLISH_DRAFT):
      return { ...state, isLoading: true };
    case getSucceeded(MESSAGE_PUBLISH_DRAFT):
      state.messageForm = {};
      state.isDraft = false;
      state.isSelected = false;
      state.selectedConversation = "";
      state.selectedPost = "";
      state.draftId = "";
      unsavedDataChecker("no");

      return { ...state, counterState: state.counterState + 1 };
    case getEnded(MESSAGE_PUBLISH_DRAFT):
      return { ...state, isLoading: false };

    case RECALC_REQUEST:
      return { ...state, isLoading: true, recalcError: null };
    case RECALC_SUCCESS:
      console.log("TRIGGERED")
      console.log("action.payload", action.payload)
      return { ...state, isLoading: false, convertSuccess: true, };
    case CLEAR_RECALC_SUCCESS:
      return {
        ...state,
        convertSuccess: false,
      };
    case RECALC_FAILURE:
      return { ...state, isLoading: false, recalcError: action.payload };

    default:
      return state;
  }
}

export default reducer;



// WEBPACK FOOTER //
// ./src/reducers/messageReducer.js